import React, { FunctionComponent, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import {
  Grid,
  MenuItem,
} from '@mui/material';
import {
  DarkGreenButton,
  Select,
  TextButton,
  TextInput,
  TransparentButton,
  Typography
} from '../../../components/v2/styled';
import { Birthdate } from '../../../components/v2/styled/Birthdate';
import { ErrorBox } from '../../../components/v2/styled/ErrorBox';
import { ZichtAccountDutch } from '../../../components/v2/styled/ZichtAccountDutch';
import { DropzoneArea } from 'mui-file-dropzone';

import { Guardian } from '../../../models/Guardian';
import { User } from '../../../models/User';

var he = require('he');
const { DateTime } = require("luxon");

type Props = {
  setStep: (n:number) => void;
  user?: User;
  setUser: (u:User) => void;
  guardian?: Guardian;
  setGuardian: (u:Guardian) => void;
  onSubmit: () => void;
  loading?: boolean;
}

export const EmissionChoiceMinorDutch: FunctionComponent<Props> = ({setStep, user, setUser, onSubmit, guardian, setGuardian, loading}) => {
  const theme = useTheme();
  const {t, i18n} = useTranslation(['translations']);

  const [isOkay, setIsOkay] = useState<boolean>(false);
  const [samesiesAddress, setSamesiesAddress] = useState<boolean>(true);
  const [samesiesContact, setSamesiesContact] = useState<boolean>(true);
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const isValidDutchZip = (zip: string) => {
    return /^[1-9][0-9]{3}\s?[a-zA-Z]{2}$/.test(zip.trim());
  };

  const isValidDutchPhone = (phone: string) => {
    const cleanPhone = phone.replace(/[\s\-()]/g, '');
    return /^((\+31)|(0031)|0)[1-9][0-9]{8}$/.test(cleanPhone);
  };

  const isUserBirthdateInvalid = !user?.birth_date || !DateTime.fromFormat(user.birth_date, "dd/LL/y").isValid || DateTime.fromFormat(user.birth_date, "dd/LL/y") < DateTime.now().minus({year: 18});
  const isGuardianBirthdateInvalid = !guardian?.birth_date || !DateTime.fromFormat(guardian.birth_date, "dd/LL/y").isValid || DateTime.fromFormat(guardian.birth_date, "dd/LL/y") > DateTime.now().minus({year: 18});

  const gender = user?.gender || 'M';

  const isFormValid =
    !!user?.first_name &&
    !!user.last_name &&
    !isUserBirthdateInvalid &&
    !!gender &&
    !!user.phone &&
    // isValidDutchPhone(user.phone) &&
    user?.email === user.repeat_email &&
    !!user.street &&
    !!user.number &&
    !!user.zip &&
    (user.country !== "NL" || (user.country === "NL" && !!isValidDutchZip(user.zip))) &&
    !!user.city &&
    !!user.zicht_account &&
    isOkay &&
    !!user.identity &&
    !!guardian?.first_name &&
    !!guardian.last_name &&
    !isGuardianBirthdateInvalid &&
    !!guardian.identity &&
    (samesiesContact || (
      !!guardian.email &&
      guardian.email === guardian.repeat_email &&
      !!guardian.phone // &&
      // isValidDutchPhone(guardian.phone)
    )) &&
    (samesiesAddress || (
      !!guardian.street &&
      !!guardian.number &&
      !!guardian.zip &&
      (guardian.country !== "NL" || (guardian.country === "NL" && !!isValidDutchZip(guardian.zip))) &&
      !!guardian.city
    ));

  return <>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="h3" sx={{
        marginTop: 4,
        display: 'inline-block',
      }}>{t("nw.emission.choice_me_new_itsme.title_personal_ven")}</Typography>
    </Grid>
    <Grid item xs={3}>
      <TextInput
        value={user?.voorletters || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            voorletters: v.toString().toUpperCase()
          });
        }}
        starred
        caption={t("nw.nl.user_info.voorletters")}
        isError={showErrors && !user?.voorletters}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={4}>
      <TextInput
        value={user?.first_name || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            first_name: v.toString()
          });
        }}
        starred
        caption={t("nw.nl.user_info.first_name")}
        isError={showErrors && !user?.first_name}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={5}>
      <TextInput
        value={user?.last_name || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            last_name: v.toString()
          });
        }}
        starred
        caption={t("nw.user_info.last_name")}
        isError={showErrors && !user?.last_name}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={7}>
      <Birthdate
        value={user?.birth_date || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            birth_date: v.target.value
          });
        }}
        starred
        caption={t("nw.user_info.birth_date")}
        // isError={showErrors ? isUserBirthdateInvalid : (!!user?.birth_date && isUserBirthdateInvalid)}
        isError={(!!user?.birth_date || showErrors) && isUserBirthdateInvalid}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={5}>
      <Select
        id="subjects"
        caption={t("nw.user_info.gender")}
        fullWidth
        value={user?.gender || "M"}
        setValue={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            gender: v.toString()
          });
        }}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          height: '40px',
          paddingTop: 0.25,
          paddingBottom: 0.25,
          backgroundColor: 'transparent',
          "&:hover fieldset": {
            borderColor: 'transparent !important'
          }
        }}
      >
        <MenuItem value="M">{he.decode((t("nw.user_info.genders.male")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
        <MenuItem value="V">{he.decode((t("nw.user_info.genders.female")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
      </Select>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="h3" sx={{
        marginTop: 4,
        display: 'inline-block',
      }}>{t("nw.emission.choice_me_new_itsme.title_personal_guardian")}</Typography>
    </Grid>
    <Grid item xs={3}>
      <TextInput
        value={guardian?.voorletters || ''}
        onChange={(v) => {
          setGuardian({
            ...(guardian || {
              auth_token: '',
              email: ''
            }),
            voorletters: v.toString().toUpperCase()
          });
        }}
        starred
        caption={t("nw.nl.user_info.voorletters")}
        isError={showErrors && !guardian?.voorletters}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={4}>
      <TextInput
        value={guardian?.first_name || ''}
        onChange={(v) => {
          setGuardian({
            ...(guardian || {
              auth_token: '',
              email: ''
            }),
            first_name: v.toString()
          });
        }}
        starred
        caption={t("nw.nl.user_info.first_name")}
        isError={showErrors && !guardian?.first_name}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={5}>
      <TextInput
        value={guardian?.last_name || ''}
        onChange={(v) => {
          setGuardian({
            ...(guardian || {
              auth_token: '',
              email: ''
            }),
            last_name: v.toString()
          });
        }}
        starred
        caption={t("nw.user_info.last_name")}
        isError={showErrors && !guardian?.last_name}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={7}>
      <Birthdate
        value={guardian?.birth_date || ''}
        onChange={(v) => {
          setGuardian({
            ...(guardian || {
              auth_token: '',
              email: ''
            }),
            birth_date: v.target.value
          });
        }}
        starred
        caption={t("nw.user_info.birth_date")}
        // isError={showErrors ? isGuardianBirthdateInvalid : (!!guardian?.birth_date && isGuardianBirthdateInvalid)}
        isError={(!!guardian?.birth_date || showErrors) && isGuardianBirthdateInvalid}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    {!!guardian?.birth_date && DateTime.fromFormat(guardian?.birth_date, "dd/LL/y") > DateTime.now().minus({year: 18}) && <Grid item xs={12}>
      <ErrorBox
        type="error"
        noTitle
        text={t("nw.emission.choice_me_new_itsme.error_age")}
      />
    </Grid>}
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="h3" sx={{
        marginTop: 4,
        display: 'inline-block',
      }}>{t("nw.emission.choice_me_new_itsme.title_contact_extra")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="subtitle1" sx={{
        display: 'inline-block',
        fontStyle: 'italic'
      }}>{t("nw.emission.choice_me_new_itsme.title_contact_extra_email")}</Typography>
    </Grid>
    <Grid item xs={12} md={6} container spacing={2}>
      <Grid item xs={12} sx={{textAlign: 'center'}}>
        <Typography variant="subtitle1" sx={{
          display: 'inline-block',
        }}>{t("nw.emission.choice_me_new_itsme.title_personal_ven")}</Typography>
      </Grid>
      <Grid item xs={12}>
        <TextInput
          value={user?.email || ''}
          onChange={(v) => {
            setUser({
              ...(user || {
                auth_token: '',
                email: ''
              }),
              email: v
            });
            if (samesiesContact) setGuardian({
              ...(guardian || {
                auth_token: '',
                email: ''
              }),
              email: v
            });
          }}
          caption={t("nw.user_info.email")}
          isError={(showErrors && (!user?.email || (!!user?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user?.email))) || (!!user?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user?.email)))}
          sx={{
            borderRadius: "8px",
            width: '100%',
            border: "2px solid transparent",
            borderColor: theme.palette.primary.dark,
            paddingTop: 0.25,
            paddingBottom: 0.25,
            height: '40px',
            backgroundColor: 'transparent',
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          value={user?.repeat_email || ''}
          onChange={(v) => {
            setUser({
              ...(user || {
                auth_token: '',
                email: ''
              }),
              repeat_email: v
            });
            if (samesiesContact) setGuardian({
              ...(guardian || {
                auth_token: '',
                email: ''
              }),
              repeat_email: v
            });
          }}
          starred
          caption={t("nw.user_info.repeat_email")}
          isError={!!user?.repeat_email && user?.email !== user?.repeat_email}
          sx={{
            borderRadius: "8px",
            width: '100%',
            border: "2px solid transparent",
            borderColor: theme.palette.primary.dark,
            paddingTop: 0.25,
            paddingBottom: 0.25,
            height: '40px',
            backgroundColor: 'transparent',
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          value={user?.phone || ''}
          onChange={(v) => {
            setUser({
              ...(user || {
                auth_token: '',
                email: ''
              }),
              phone: v.toString()
            });
            if (samesiesContact) setGuardian({
              ...(guardian || {
                auth_token: '',
                email: ''
              }),
              phone: v.toString()
            });
          }}
          starred
          caption={t("nw.nl.user_info.phone")}
          isError={!!showErrors && !user?.phone}
          placeholder="0031 6 12345678"
          sx={{
            borderRadius: "8px",
            width: '100%',
            border: "2px solid transparent",
            borderColor: theme.palette.primary.dark,
            paddingTop: 0.25,
            paddingBottom: 0.25,
            height: '40px',
            backgroundColor: 'transparent',
          }}
        />
      </Grid>
    </Grid>
    <Grid item xs={12} md={6} container spacing={2} sx={{alignContent: 'baseline'}}>
      <Grid item xs={12} sx={{textAlign: 'center'}}>
        <Typography variant="subtitle1" sx={{
          display: 'inline-block',
        }}>{t("nw.emission.choice_me_new_itsme.title_personal_guardian")}</Typography>
      </Grid>
      {!!samesiesContact && <Grid item xs={12}>
        <TransparentButton
          label={t("nw.emission.choice_minor.contact_details_diff")}
          id='contact_details_diff'
          onClick={() => {
            setSamesiesContact(false);
          }}
          sx={{
            marginTop: 3
          }}
        />
      </Grid>}
      {!samesiesContact && <>
        <Grid item xs={12}>
          <TextInput
            value={guardian?.email || ''}
            onChange={(v) => {
              setGuardian({
                ...(guardian || {
                  auth_token: '',
                  email: ''
                }),
                email: v
              });
            }}
            starred
            caption={t("nw.user_info.email")}
            // isError={showErrors && !guardian?.email}
            isError={(showErrors && (!guardian?.email || (!!guardian?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guardian?.email))) || (!!guardian?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guardian?.email)))}
            sx={{
              borderRadius: "8px",
              width: '100%',
              border: "2px solid transparent",
              borderColor: theme.palette.primary.dark,
              paddingTop: 0.25,
              paddingBottom: 0.25,
              height: '40px',
              backgroundColor: 'transparent',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            value={guardian?.repeat_email || ''}
            onChange={(v) => {
              setGuardian({
                ...(guardian || {
                  auth_token: '',
                  email: ''
                }),
                repeat_email: v
              });
            }}
            starred
            caption={t("nw.user_info.repeat_email")}
            isError={!samesiesContact && !!guardian?.repeat_email && guardian?.email !== guardian?.repeat_email}
            sx={{
              borderRadius: "8px",
              width: '100%',
              border: "2px solid transparent",
              borderColor: theme.palette.primary.dark,
              paddingTop: 0.25,
              paddingBottom: 0.25,
              height: '40px',
              backgroundColor: 'transparent',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            value={guardian?.phone || ''}
            onChange={(v) => {
              setGuardian({
                ...(guardian || {
                  auth_token: '',
                  email: ''
                }),
                phone: v.toString()
              });
            }}
            starred
            caption={t("nw.nl.user_info.phone")}
            isError={!!showErrors && !samesiesContact && !guardian?.phone}
            placeholder="0031 6 12345678"
            sx={{
              borderRadius: "8px",
              width: '100%',
              border: "2px solid transparent",
              borderColor: theme.palette.primary.dark,
              paddingTop: 0.25,
              paddingBottom: 0.25,
              height: '40px',
              backgroundColor: 'transparent',
            }}
          />
        </Grid>
      </>}
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="h3" sx={{
        marginTop: 4,
        display: 'inline-block',
      }}>{t("nw.emission.choice_me_new_itsme.title_address")}</Typography>
    </Grid>
    <Grid item xs={12} md={6} container spacing={2} sx={{alignContent: 'baseline'}}>
      <Grid item xs={12} sx={{textAlign: 'center'}}>
        <Typography variant="subtitle1" sx={{
          display: 'inline-block',
        }}>{t("nw.emission.choice_me_new_itsme.title_personal_ven")}</Typography>
      </Grid>
      <Grid item xs={8}>
        <TextInput
          value={user?.street || ''}
          onChange={(v) => {
            setUser({
              ...(user || {
                auth_token: '',
                email: ''
              }),
              street: v
            });
            if (samesiesAddress) setGuardian({
              ...(guardian || {
                auth_token: '',
                email: ''
              }),
              street: v
            });
          }}
          starred
          caption={t("nw.user_info.street")}
          isError={showErrors && !user?.street}
          sx={{
            borderRadius: "8px",
            width: '100%',
            border: "2px solid transparent",
            borderColor: theme.palette.primary.dark,
            paddingTop: 0.25,
            paddingBottom: 0.25,
            height: '40px',
            backgroundColor: 'transparent',
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <TextInput
          value={user?.number || ''}
          onChange={(v) => {
            setUser({
              ...(user || {
                auth_token: '',
                email: ''
              }),
              number: v
            });
            if (samesiesAddress) setGuardian({
              ...(guardian || {
                auth_token: '',
                email: ''
              }),
              number: v
            });
          }}
          starred
          caption={t("nw.nl.user_info.number")}
          isError={showErrors && !user?.number}
          sx={{
            borderRadius: "8px",
            width: '100%',
            border: "2px solid transparent",
            borderColor: theme.palette.primary.dark,
            paddingTop: 0.25,
            paddingBottom: 0.25,
            height: '40px',
            backgroundColor: 'transparent',
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <TextInput
          value={user?.zip || ''}
          onChange={(v) => {
            setUser({
              ...(user || {
                auth_token: '',
                email: ''
              }),
              zip: v
            });
            if (samesiesAddress) setGuardian({
              ...(guardian || {
                auth_token: '',
                email: ''
              }),
              zip: v
            });
          }}
          starred
          caption={t("nw.nl.user_info.zip")}
          isError={showErrors ? (!user?.zip || (user.country === "NL" && !isValidDutchZip(user.zip))) : (!!user?.zip && (user.country === "NL" && !isValidDutchZip(user.zip)))}
          sx={{
            borderRadius: "8px",
            width: '100%',
            border: "2px solid transparent",
            borderColor: theme.palette.primary.dark,
            paddingTop: 0.25,
            paddingBottom: 0.25,
            height: '40px',
            backgroundColor: 'transparent',
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          value={user?.city || ''}
          onChange={(v) => {
            setUser({
              ...(user || {
                auth_token: '',
                email: ''
              }),
              city: v
            });
            if (samesiesAddress) setGuardian({
              ...(guardian || {
                auth_token: '',
                email: ''
              }),
              city: v
            });
          }}
          starred
          caption={t("nw.user_info.city")}
          isError={showErrors && !user?.city}
          sx={{
            borderRadius: "8px",
            width: '100%',
            border: "2px solid transparent",
            borderColor: theme.palette.primary.dark,
            paddingTop: 0.25,
            paddingBottom: 0.25,
            height: '40px',
            backgroundColor: 'transparent',
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <Select
          id="country"
          caption={t("nw.user_info.country")}
          fullWidth
          value={user?.country || "NL"}
          setValue={(v) => {
            setUser({
              ...(user || {
                auth_token: '',
                email: ''
              }),
              country: v.toString()
            });
            if (samesiesAddress) setGuardian({
              ...(guardian || {
                auth_token: '',
                email: ''
              }),
              country: v?.toString()
            });
          }}
          sx={{
            borderRadius: "8px",
            width: '100%',
            border: "2px solid transparent",
            borderColor: theme.palette.primary.dark,
            height: '40px',
            paddingTop: 0.25,
            paddingBottom: 0.25,
            backgroundColor: 'transparent',
            "&:hover fieldset": {
              borderColor: 'transparent !important'
            }
          }}
        >
          <MenuItem value="NL">{he.decode((t("nw.user_info.countries.nl")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
          <MenuItem value="ARU">{he.decode((t("nw.user_info.countries.nl_aruba")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
          <MenuItem value="SMA">{he.decode((t("nw.user_info.countries.nl_sintmaarten")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
          <MenuItem value="CUR">{he.decode((t("nw.user_info.countries.nl_curacao")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
          <MenuItem value="OTHR">{he.decode((t("nw.user_info.countries.nl_other")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
        </Select>
      </Grid>
    </Grid>
    <Grid item xs={12} md={6} container spacing={2} sx={{alignContent: 'baseline'}}>
      <Grid item xs={12} sx={{textAlign: 'center'}}>
        <Typography variant="subtitle1" sx={{
          display: 'inline-block',
        }}>{t("nw.emission.choice_me_new_itsme.title_personal_guardian")}</Typography>
      </Grid>
      {!!samesiesAddress && <Grid item xs={12}>
        <TransparentButton
          label={t("nw.emission.choice_minor.address_details_diff")}
          id='address_details_diff'
          onClick={() => {
            setSamesiesAddress(false);
          }}
          sx={{
            marginTop: 3
          }}
        />
      </Grid>}
      {!samesiesAddress && <>
        <Grid item xs={8}>
          <TextInput
            value={guardian?.street || ''}
            onChange={(v) => {
              setGuardian({
                ...(guardian || {
                  auth_token: '',
                  email: ''
                }),
                street: v
              });
            }}
            starred
            caption={t("nw.user_info.street")}
            isError={showErrors && !guardian?.street}
            sx={{
              borderRadius: "8px",
              width: '100%',
              border: "2px solid transparent",
              borderColor: theme.palette.primary.dark,
              paddingTop: 0.25,
              paddingBottom: 0.25,
              height: '40px',
              backgroundColor: 'transparent',
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            value={guardian?.number || ''}
            onChange={(v) => {
              setGuardian({
                ...(guardian || {
                  auth_token: '',
                  email: ''
                }),
                number: v
              });
            }}
            starred
            caption={t("nw.nl.user_info.number")}
            isError={showErrors && !guardian?.number}
            sx={{
              borderRadius: "8px",
              width: '100%',
              border: "2px solid transparent",
              borderColor: theme.palette.primary.dark,
              paddingTop: 0.25,
              paddingBottom: 0.25,
              height: '40px',
              backgroundColor: 'transparent',
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <TextInput
            value={guardian?.zip || ''}
            onChange={(v) => {
              setGuardian({
                ...(guardian || {
                  auth_token: '',
                  email: ''
                }),
                zip: v
              });
            }}
            starred
            caption={t("nw.nl.user_info.zip")}
            // isError={!samesiesAddress && !!guardian?.zip && !isValidDutchZip(guardian.zip)}
            isError={!samesiesAddress && (showErrors ? (!guardian?.zip || (guardian.country === "NL" && !isValidDutchZip(guardian.zip))) : (!!guardian?.zip && (guardian.country === "NL" && !isValidDutchZip(guardian.zip))))}
            sx={{
              borderRadius: "8px",
              width: '100%',
              border: "2px solid transparent",
              borderColor: theme.palette.primary.dark,
              paddingTop: 0.25,
              paddingBottom: 0.25,
              height: '40px',
              backgroundColor: 'transparent',
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            value={guardian?.city || ''}
            onChange={(v) => {
              setGuardian({
                ...(guardian || {
                  auth_token: '',
                  email: ''
                }),
                city: v
              });
            }}
            starred
            caption={t("nw.user_info.city")}
            isError={showErrors && !guardian?.city}
            sx={{
              borderRadius: "8px",
              width: '100%',
              border: "2px solid transparent",
              borderColor: theme.palette.primary.dark,
              paddingTop: 0.25,
              paddingBottom: 0.25,
              height: '40px',
              backgroundColor: 'transparent',
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Select
            id="country"
            caption={t("nw.user_info.country")}
            fullWidth
            value={guardian?.country || "NL"}
            setValue={(v) => {
              setGuardian({
                ...(guardian || {
                  auth_token: '',
                  email: ''
                }),
                country: v?.toString()
              });
            }}
            sx={{
              borderRadius: "8px",
              width: '100%',
              border: "2px solid transparent",
              borderColor: theme.palette.primary.dark,
              height: '40px',
              paddingTop: 0.25,
              paddingBottom: 0.25,
              backgroundColor: 'transparent',
              "&:hover fieldset": {
                borderColor: 'transparent !important'
              }
            }}
          >
            <MenuItem value="NL">{he.decode((t("nw.user_info.countries.nl")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
            <MenuItem value="ARU">{he.decode((t("nw.user_info.countries.nl_aruba")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
            <MenuItem value="SMA">{he.decode((t("nw.user_info.countries.nl_sintmaarten")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
            <MenuItem value="CUR">{he.decode((t("nw.user_info.countries.nl_curacao")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
            <MenuItem value="OTHR">{he.decode((t("nw.user_info.countries.nl_other")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
          </Select>
        </Grid>
      </>}
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="h3" sx={{
        marginTop: 4,
        display: 'inline-block',
      }}>{t("nw.emission.choice_me_new_itsme.title_banking")}</Typography>
    </Grid>
    <Grid item xs={12}>
      <ZichtAccountDutch
        value={user?.zicht_account || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            zicht_account: v.target.value
          });
        }}
        setExternalIsOkay={setIsOkay}
        starred
        caption={t("nw.nl.user_info.zicht_account")}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="h3" sx={{
        marginTop: 4,
        display: 'inline-block',
      }}>{t("nw.nl.emission.choice_me_new_itsme.title_identity_short")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="subtitle1" sx={{
        display: 'inline-block',
        fontStyle: 'italic'
      }}>{t("nw.nl.emission.choice_me_new_itsme.title_identity_short_extra_subline")}</Typography>
    </Grid>
    <Grid item xs={12} md={6} container spacing={2} sx={{alignContent: 'baseline'}}>
      <Grid item xs={12} sx={{textAlign: 'center'}}>
        <Typography variant="subtitle1" html fuck_p_tags sx={{
          display: 'inline-block',
        }}>{t("nw.nl.emission.choice_minor.title_identity_ven")}</Typography>
      </Grid>
      <Grid item xs={12} sx={{
        display: 'flex',
        "& .cust_dropzonearea > div > div": {
          flexBasis: 'auto !important',
          marginLeft: 'auto',
          marginRight: 'auto'
        }
      }}>
        <DropzoneArea
          acceptedFiles={['image/gif', 'image/jpeg', 'image/png', '.pdf']}
          filesLimit={1}
          fileObjects={[]}
          dropzoneText={he.decode(t('nw.general.dropzone_text').replace(/<[^>]*>?/gm, ''))}
          getDropRejectMessage={(rejectedFile, x, y) => {
            return he.decode(t("nw.general_error.dropzone.rejected", {name: rejectedFile.name}).replace(/<[^>]*>?/gm, ''));
          }}
          onChange={(files) => {
            setUser({
              ...(user || {
                auth_token: '',
                email: ''
              }),
              identity: files[0]
            });
          }}
          maxFileSize={3000000}
          previewGridProps={{item: {style: {maxWidth: '100%', flexBasis: '100%'}}}}
          dropzoneClass="cust_dropzonearea"
        />
      </Grid>
    </Grid>
    <Grid item xs={12} md={6} container spacing={2} sx={{alignContent: 'baseline'}}>
      <Grid item xs={12} sx={{textAlign: 'center'}}>
        <Typography variant="subtitle1" html fuck_p_tags sx={{
          display: 'inline-block',
        }}>{t("nw.nl.emission.choice_minor.title_identity_guardian")}</Typography>
      </Grid>
      <Grid item xs={12} sx={{
        display: 'flex',
        "& .cust_dropzonearea > div > div": {
          flexBasis: 'auto !important',
          marginLeft: 'auto',
          marginRight: 'auto'
        }
      }}>
        <DropzoneArea
          acceptedFiles={['image/gif', 'image/jpeg', 'image/png', '.pdf']}
          filesLimit={1}
          fileObjects={[]}
          dropzoneText={he.decode(t('nw.general.dropzone_text').replace(/<[^>]*>?/gm, ''))}
          getDropRejectMessage={(rejectedFile, x, y) => {
            return he.decode(t("nw.general_error.dropzone.rejected", {name: rejectedFile.name}).replace(/<[^>]*>?/gm, ''));
          }}
          onChange={(files) => {
            setGuardian({
              ...(guardian || {
                auth_token: '',
                email: ''
              }),
              identity: files[0]
            });
          }}
          maxFileSize={3000000}
          previewGridProps={{item: {style: {maxWidth: '100%', flexBasis: '100%'}}}}
          dropzoneClass="cust_dropzonearea"
        />
      </Grid>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="h3" sx={{
        marginTop: 4,
        display: 'inline-block',
      }}>{t("nw.emission.choice_me_new_itsme.title_overview")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'left'}}>
      {!(!user?.voorletters || !user?.first_name || !user.last_name || !user.birth_date || !gender || !DateTime.fromFormat(user?.birth_date, "dd/LL/y").isValid || DateTime.fromFormat(user.birth_date, "dd/LL/y") < DateTime.now().minus({year: 18})) && <CheckCircleOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.primary.light}} />}
      {!!(!user?.voorletters || !user?.first_name || !user.last_name || !user.birth_date || !gender || !DateTime.fromFormat(user?.birth_date, "dd/LL/y").isValid || DateTime.fromFormat(user.birth_date, "dd/LL/y") < DateTime.now().minus({year: 18})) && <ErrorOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.warning.main}} />}
      <Typography variant="subtitle1" sx={{
        marginTop: 1,
        display: 'inline-block',
        color: (!user?.voorletters || !user?.first_name || !user.last_name || !user.birth_date || !gender || !DateTime.fromFormat(user?.birth_date, "dd/LL/y").isValid || DateTime.fromFormat(user.birth_date, "dd/LL/y") < DateTime.now().minus({year: 18})) ? theme.palette.warning.main : theme.palette.primary.light,
      }}>{t("nw.emission.choice_me_new_itsme.title_personal_ven")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'left'}}>
      {!(!guardian?.voorletters || !guardian?.first_name || !guardian.last_name || !guardian.birth_date || !DateTime.fromFormat(guardian?.birth_date, "dd/LL/y").isValid || DateTime.fromFormat(guardian?.birth_date, "dd/LL/y") > DateTime.now().minus({year: 18})) && <CheckCircleOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.primary.light}} />}
      {!!(!guardian?.voorletters || !guardian?.first_name || !guardian.last_name || !guardian.birth_date || !DateTime.fromFormat(guardian?.birth_date, "dd/LL/y").isValid || DateTime.fromFormat(guardian?.birth_date, "dd/LL/y") > DateTime.now().minus({year: 18})) && <ErrorOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.warning.main}} />}
      <Typography variant="subtitle1" sx={{
        marginTop: 1,
        display: 'inline-block',
        color: (!guardian?.voorletters || !guardian?.first_name || !guardian.last_name || !guardian.birth_date || !DateTime.fromFormat(guardian?.birth_date, "dd/LL/y").isValid || DateTime.fromFormat(guardian?.birth_date, "dd/LL/y") > DateTime.now().minus({year: 18})) ? theme.palette.warning.main : theme.palette.primary.light,
      }}>{t("nw.emission.choice_me_new_itsme.title_personal_guardian")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'left'}}>
      {!(!user?.phone || user?.email !== user.repeat_email || (!samesiesContact && (!guardian?.email || !guardian.repeat_email || guardian.email !== guardian.repeat_email || !guardian.phone))) && <CheckCircleOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.primary.light}} />}
      {!!(!user?.phone || user?.email !== user.repeat_email || (!samesiesContact && (!guardian?.email || !guardian.repeat_email || guardian.email !== guardian.repeat_email || !guardian.phone))) && <ErrorOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.warning.main}} />}
      <Typography variant="subtitle1" sx={{
        marginTop: 1,
        display: 'inline-block',
        color: (!user?.phone || user?.email !== user.repeat_email || (!samesiesContact && (!guardian?.email || !guardian.repeat_email || guardian.email !== guardian.repeat_email || !guardian.phone))) ? theme.palette.warning.main : theme.palette.primary.light,
      }}>{t("nw.emission.choice_me_new_itsme.title_contact_extra")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'left'}}>
      {!(!user?.street || !user.number || !user.zip || (user.country === "NL" && !isValidDutchZip(user.zip)) || !user.city || (!samesiesAddress && (!guardian?.street || !guardian.number || !guardian.zip || (guardian.country === "NL" && !isValidDutchZip(guardian.zip)) || !guardian.city))) && <CheckCircleOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.primary.light}} />}
      {!!(!user?.street || !user.number || !user.zip || (user.country === "NL" && !isValidDutchZip(user.zip)) || !user.city || (!samesiesAddress && (!guardian?.street || !guardian.number || !guardian.zip || (guardian.country === "NL" && !isValidDutchZip(guardian.zip)) || !guardian.city))) && <ErrorOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.warning.main}} />}
      <Typography variant="subtitle1" sx={{
        marginTop: 1,
        display: 'inline-block',
        color: (!user?.street || !user.number || !user.zip || (user.country === "NL" && !isValidDutchZip(user.zip)) || !user.city || (!samesiesAddress && (!guardian?.street || !guardian.number || !guardian.zip || (guardian.country === "NL" && !isValidDutchZip(guardian.zip)) || !guardian.city))) ? theme.palette.warning.main : theme.palette.primary.light,
      }}>{t("nw.emission.choice_me_new_itsme.title_address")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'left'}}>
      {!(!user?.zicht_account || !isOkay) && <CheckCircleOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.primary.light}} />}
      {!!(!user?.zicht_account || !isOkay) && <ErrorOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.warning.main}} />}
      <Typography variant="subtitle1" sx={{
        marginTop: 1,
        display: 'inline-block',
        color: (!user?.zicht_account || !isOkay) ? theme.palette.warning.main : theme.palette.primary.light,
      }}>{t("nw.emission.choice_me_new_itsme.title_banking")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'left'}}>
      {!(!user?.identity || !guardian?.identity) && <CheckCircleOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.primary.light}} />}
      {!!(!user?.identity || !guardian?.identity) && <ErrorOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.warning.main}} />}
      <Typography variant="subtitle1" sx={{
        marginTop: 1,
        display: 'inline-block',
        color: (!user?.identity || !guardian?.identity) ? theme.palette.warning.main : theme.palette.primary.light,
      }}>{t("nw.nl.emission.choice_me_new_itsme.title_identity_short")}</Typography>
    </Grid>

    <Grid item xs={12} sx={{textAlign: 'center', paddingTop: 4, paddingRight: {xs: 0, md: 2.5}}}>
      <TextButton
        id="back_btn"
        label={t("nw.emission.forgot.cta_back")}
        onClick={() => {
          setStep(15);
          window.scrollTo(0, 0);
        }}
        sx={{
          marginLeft: 1,
          marginRight: 1,
          marginTop: 1
        }}
      />
      <DarkGreenButton
        id="next_btn"
        label={t("nw.emission.choice_me_new_itsme.cta_next")}
        loading={!!loading}
        onClick={() => {
          if (isFormValid) {
            onSubmit();
          } else {
            setShowErrors(true);
            window.scrollTo(0, 0);
          }
        }}
        sx={{
          marginLeft: 1,
          marginRight: 1,
          marginTop: 1,
          color: 'white !important'
        }}
      />
    </Grid>
  </>;
}
